/** @jsx jsx */
import type { Interpolation } from '@emotion/react';
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import Imgix from 'js/components/Imgix';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Button, Dialog, Typography2, breakpoints } from '@coursera/cds-core';
import type { ButtonProps, Theme } from '@coursera/cds-core';
import { SvgArrowRight } from '@coursera/coursera-ui/svg';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import { logo } from 'bundles/coursera-plus-common/utils/courseraPlusUtils';
import { getPriceDisplayFromAmount } from 'bundles/coursera-plus-common/utils/generalUtils';
import type SubscriptionsV1 from 'bundles/naptimejs/resources/subscriptions.v1';
import type { ProductPrices } from 'bundles/ownable-products-common/hooks/useGetProductPrices';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/my-purchases-graphql';

type PropsToComponent = {
  isOpen: boolean;
  onClose: () => void;
  openAnnualUpgradeModal: () => void;
  monthlySavings: 0 | JSX.Element | null;
  courseraPlusAnnualPrice?: ProductPrices;
  subscription: SubscriptionsV1;
};

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

const styles: Record<string, Interpolation<Theme>> = {
  topLogo: css`
    margin-bottom: 24px;
    padding-left: 48px;
    ${breakpoints.down('sm')} {
      margin-top: 2px;
    }
    ${breakpoints.down('xs')} {
      padding-left: 16px;
    }
  `,
  actions: css`
    padding-top: 16px;
  `,
  subtitle: css`
    line-height: 28px;
  `,
};

const StartAnnualUpgradeModal = ({
  isOpen,
  onClose,
  openAnnualUpgradeModal,
  monthlySavings,
  courseraPlusAnnualPrice,
  subscription,
}: PropsToComponent) => {
  if (!monthlySavings || !courseraPlusAnnualPrice) {
    return null;
  }
  const annualPriceDisplay = getPriceDisplayFromAmount(
    courseraPlusAnnualPrice.amount,
    courseraPlusAnnualPrice.currencyCode
  );
  const { Content, HeadingGroup, Actions } = Dialog;

  const handleCloseModal = () => {
    onClose();
  };
  return (
    <Dialog
      aria-describedby="dialog-content"
      open={isOpen}
      variant="standard"
      width="medium"
      onClose={handleCloseModal}
      data-testid="start-annual-upgrade-modal"
    >
      <Imgix src={logo.PLUS_FULL_BLUE} alt={_t('Coursera Plus logo')} maxWidth={200} css={styles.topLogo} />
      <HeadingGroup>{_t('Switch to Coursera Plus annual and save')}</HeadingGroup>
      <Content>
        <TrackedDiv
          withVisibilityTracking={true}
          trackingName="start_annual_upgrade_modal"
          trackingData={{ courseraPlusSubscriptionId: subscription?.id }}
        >
          <Typography2 variant="subtitleLarge" component="p" css={styles.subtitle}>
            <FormattedMessage
              message={_t(
                `Reach your goals with a year of Coursera Plus. You'll save {monthlySavings} monthly ({annualPriceDisplay} billed annually).`
              )}
              dash={<span>&#8212;</span>}
              monthlySavings={monthlySavings}
              annualPriceDisplay={annualPriceDisplay}
            />
          </Typography2>
        </TrackedDiv>
      </Content>
      <Actions>
        <TrackedButton
          variant="primary"
          onClick={openAnnualUpgradeModal}
          icon={<SvgArrowRight color="var(--cds-color-white-0)" size={18} />}
          data-testid="start-annual-upgrade-continue-button"
          trackingName="start_annual_upgrade_continue_button"
        >
          {_t('Continue')}
        </TrackedButton>
      </Actions>
    </Dialog>
  );
};

export default StartAnnualUpgradeModal;
